import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Plus, Target, X } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiDetail } from "../../lib/api";
import {
  STAGE_LABEL,
  OUTREACH_ACTIVE_COLUMNS,
  MONTHLY_ENGAGEMENT_GOAL,
  SCOPE_REVIEW_STATUS_LABEL,
  compareClientsByCreatedDesc,
  compensationExchangeLabel,
  isEngagementBucketStage,
  normalizeEngagementStage,
  outreachBoardColumnKey,
  prospectScopeLabel,
} from "../../lib/engagementStages";
import { canonicalOffering } from "../../config/offeringPhases";
import { MotionSection } from "../../components/Motion";
import { cn } from "../../lib/utils";
import { isLocalApp } from "../../lib/env";
import {
  allMockBarterClients,
  isMockBarterClientId,
} from "../../lib/mockBarterClients";

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const TYPE_LABEL_SHORT = {
  brand_design: "Brand",
  web_design: "Web",
  product_design: "Product",
  operational_ecosystem: "Ops",
};
const STAGE_ACCENT = {
  outreach: "#9AA3B2",
  met: "#001EFF",
  scope: "#001EFF",
  archive: "#B0B6C2",
};

const COLUMN_BAR = STAGE_ACCENT;

/** Left rail color: grey inquiry · blue met · pink awaiting response · blue scope+invoice otherwise. */
function cardBarColor(client) {
  const col = boardColumnKey(client);
  if (col === "outreach") return "#9AA3B2";
  if (col === "met") return "#001EFF";
  if (col === "scope") {
    const review = client?.scope_review?.status;
    if (review === "pending") return "var(--t-pink)";
    return "#001EFF";
  }
  return "#9AA3B2";
}

function boardColumnKey(clientOrStage) {
  return outreachBoardColumnKey(clientOrStage);
}

function isThisMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function monthLabel() {
  return new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function CreateProspectModal({ onClose, onCreated }) {
  const reduce = useReducedMotion();
  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    service_tier: "web_design",
    engagement_type: "project",
    engagement_stage: "outreach",
    compensation_type: "cash",
    prospect_notes: "",
    owner: "Taī",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/clients", {
        ...form,
        contact_email: form.contact_email.trim() || null,
        kind: "prospect",
        send_invite: false,
        rate: null,
      });
      toast.success("Inquiry added");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create inquiry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/25 p-0 sm:p-4"
      onClick={onClose}
      data-testid="create-prospect-modal"
    >
      <motion.aside
        initial={reduce ? false : { x: 28, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="neo-panel neo-drawer flex h-full w-full max-w-md flex-col overflow-hidden rounded-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--t-border)] px-6 py-5">
          <div className="min-w-0">
            <div className="label-eyebrow mb-2">Inbound</div>
            <div className="font-heading truncate text-2xl tracking-tight !font-extralight">
              New inquiry
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="t-scroll-soft flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="label-eyebrow mb-2 block">Name / studio</label>
              <input
                className="t-input !rounded-xl !bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="prospect-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow mb-2 block">Contact name</label>
                <input
                  className="t-input !rounded-xl !bg-white"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  required
                  data-testid="prospect-form-contact-name"
                />
              </div>
              <div>
                <label className="label-eyebrow mb-2 block">Contact email</label>
                <input
                  type="email"
                  className="t-input !rounded-xl !bg-white"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="Optional"
                  data-testid="prospect-form-contact-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow mb-2 block">Type</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.service_tier}
                    onChange={(e) => setForm({ ...form, service_tier: e.target.value })}
                  >
                    <option value="brand_design">Brand design</option>
                    <option value="web_design">Web design</option>
                    <option value="product_design">Product design</option>
                    <option value="operational_ecosystem">Operational ecosystem</option>
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
              <div>
                <label className="label-eyebrow mb-2 block">Stage</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.engagement_stage}
                    onChange={(e) => setForm({ ...form, engagement_stage: e.target.value })}
                    data-testid="prospect-form-stage"
                  >
                    <option value="outreach">Inquiry</option>
                    <option value="met">Met</option>
                    <option value="proposal">
                      {form.compensation_type === "barter" ? "Agreement" : "Scope + Invoice"}
                    </option>
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Exchange</label>
              <div className="relative">
                <select
                  className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                  value={form.compensation_type}
                  onChange={(e) => {
                    const compensation_type = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      compensation_type,
                      engagement_stage:
                        compensation_type === "barter" && prev.engagement_stage === "invoice"
                          ? "proposal"
                          : prev.engagement_stage,
                    }));
                  }}
                  data-testid="prospect-form-compensation"
                >
                  <option value="cash">Invoice</option>
                  <option value="barter">In-kind</option>
                </select>
                <ChevronDown
                  size={16}
                  strokeWidth={1.6}
                  aria-hidden
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Owner</label>
              <div className="relative">
                <select
                  className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                  value={form.owner}
                  onChange={(e) => setForm({ ...form, owner: e.target.value })}
                  data-testid="prospect-form-owner"
                >
                  <option value="Taī">Taī</option>
                </select>
                <ChevronDown
                  size={16}
                  strokeWidth={1.6}
                  aria-hidden
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Notes</label>
              <textarea
                rows={3}
                className="t-textarea min-h-[96px] !rounded-xl !bg-white"
                value={form.prospect_notes}
                onChange={(e) => setForm({ ...form, prospect_notes: e.target.value })}
                data-testid="prospect-form-notes"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-[var(--t-border)] px-6 py-4">
            <button type="button" onClick={onClose} className="t-btn-secondary !rounded-xl">
              Cancel
            </button>
            <button
              type="submit"
              className="neo-cta"
              disabled={busy}
              data-testid="prospect-form-submit"
            >
              {busy ? "Saving…" : "Add"}
            </button>
          </div>
        </form>
      </motion.aside>
    </div>
  );
}

function ActionRailCell({ client, onOpen }) {
  const stage = normalizeEngagementStage(client.engagement_stage, { defaultStage: "outreach" });
  const col = boardColumnKey(client);
  const inScope = col === "scope";
  const waitingOnPayment = stage === "invoice";
  const bar = cardBarColor(client);
  const reviewStatus = client.scope_review?.status;
  const reviewLabel = reviewStatus ? SCOPE_REVIEW_STATUS_LABEL[reviewStatus] : null;
  const acceptedOption = client.scope_review?.accepted_option_label;
  const scopeWord = prospectScopeLabel(client);
  const reviewBit =
    reviewStatus === "accepted"
      ? `${scopeWord} accepted${acceptedOption ? ` · ${acceptedOption}` : ""}`
      : inScope && reviewLabel
        ? reviewLabel
        : waitingOnPayment
          ? "Waiting on payment"
          : null;
  const stageDisplay =
    stage === "proposal" || stage === "invoice" ? scopeWord : STAGE_LABEL[stage];

  return (
    <div
      className="neo-panel relative overflow-hidden p-4 text-left"
      data-testid={`outreach-card-${client.id}`}
    >
      <div className="absolute left-0 top-0 h-full w-1.5" style={{ background: bar }} />
      <div className="pl-3">
        <button
          type="button"
          className="group w-full text-left"
          onClick={() => onOpen(client)}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="font-heading min-w-0 truncate text-lg !font-light group-hover:text-[var(--t-accent)]">
              {client.name}
            </div>
            <span
              className={cn(
                "t-pill t-pill-meta shrink-0 transition-colors",
                "group-hover:border-[var(--t-accent)] group-hover:text-[var(--t-accent)]",
                "group-focus-visible:border-[var(--t-accent)] group-focus-visible:text-[var(--t-accent)]",
                "max-sm:!px-2 max-sm:!py-[2px] max-sm:!text-[9px] max-sm:!tracking-[0.12em]",
              )}
              data-testid={`outreach-exchange-${client.id}`}
            >
              {compensationExchangeLabel(client.compensation_type)}
            </span>
          </div>
          <div className="mt-1 text-xs text-[var(--t-meta)]">
            {stageDisplay} ·{" "}
            {TYPE_LABEL_SHORT[canonicalOffering(client.service_tier)] || "—"}
            {" · "}
            {client.owner?.trim() || "Unassigned"}
            {reviewBit ? (
              <span
                className={
                  reviewStatus === "accepted"
                    ? " text-[var(--t-accent)]"
                    : reviewStatus === "pending"
                      ? " text-[var(--t-pink)]"
                      : ""
                }
              >
                {" · "}
                {reviewBit}
              </span>
            ) : null}
          </div>
          {client.prospect_notes ? (
            <p className="mt-1.5 line-clamp-1 text-xs text-[var(--t-meta)]">{client.prospect_notes}</p>
          ) : null}
        </button>
      </div>
    </div>
  );
}

export default function OutreachPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const stageParamRaw = params.get("stage");
  // Legacy ?stage=invoice bookmarks land on the merged Scope + Invoice column.
  const stageParam = ["outreach", "met", "scope"].includes(stageParamRaw)
    ? stageParamRaw
    : stageParamRaw === "invoice"
      ? "scope"
      : "outreach";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [monthlyGoal, setMonthlyGoal] = useState(MONTHLY_ENGAGEMENT_GOAL);
  const [goalMonth, setGoalMonth] = useState(() => currentMonthKey());
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState(String(MONTHLY_ENGAGEMENT_GOAL));
  const [goalSaving, setGoalSaving] = useState(false);
  const goalInputRef = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (stageParamRaw !== "invoice") return;
    const next = new URLSearchParams(params);
    next.set("stage", "scope");
    setParams(next, { replace: true });
  }, [stageParamRaw, params, setParams]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: clientRows }, { data: goalData }] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/outreach-goal"),
      ]);
      setClients(clientRows || []);
      const goal = Number(goalData?.goal) || MONTHLY_ENGAGEMENT_GOAL;
      setMonthlyGoal(goal);
      setGoalMonth(goalData?.month || currentMonthKey());
      setGoalDraft(String(goal));
    } catch {
      toast.error("Could not load outreach");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!editingGoal) return undefined;
    const el = goalInputRef.current;
    if (!el) return undefined;
    el.focus();
    el.select();
    return undefined;
  }, [editingGoal]);

  const saveMonthlyGoal = async () => {
    if (goalSaving) return;
    const parsed = Number.parseInt(String(goalDraft).trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setGoalDraft(String(monthlyGoal));
      setEditingGoal(false);
      toast.error("Goal must be at least 1");
      return;
    }
    const next = Math.min(999, parsed);
    if (next === monthlyGoal) {
      setEditingGoal(false);
      setGoalDraft(String(monthlyGoal));
      return;
    }
    setGoalSaving(true);
    try {
      const { data } = await api.put("/admin/outreach-goal", {
        month: goalMonth || currentMonthKey(),
        goal: next,
      });
      const saved = Number(data?.goal) || next;
      setMonthlyGoal(saved);
      setGoalDraft(String(saved));
      setEditingGoal(false);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not save goal"));
      setGoalDraft(String(monthlyGoal));
      setEditingGoal(false);
    } finally {
      setGoalSaving(false);
    }
  };

  // Active pipeline only — archived prospects leave Outreach (Studio owns archives).
  const pipelineColumns = OUTREACH_ACTIVE_COLUMNS;

  const boardClients = useMemo(
    () => {
      const rows = clients.filter((c) => {
        const col = boardColumnKey(c);
        return col != null && col !== "archive";
      });
      if (!isLocalApp()) return rows;
      const mocks = allMockBarterClients();
      const withoutDupes = rows.filter((c) => !isMockBarterClientId(c.id));
      return [...mocks, ...withoutDupes];
    },
    [clients],
  );

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(pipelineColumns.map((c) => [c.key, []]));
    boardClients.forEach((c) => {
      const col = boardColumnKey(c);
      if (map[col]) map[col].push(c);
    });
    Object.values(map).forEach((list) => list.sort(compareClientsByCreatedDesc));
    return map;
  }, [boardClients, pipelineColumns]);

  const counts = useMemo(() => {
    const base = {};
    pipelineColumns.forEach((c) => {
      base[c.key] = byColumn[c.key]?.length || 0;
    });
    return base;
  }, [byColumn, pipelineColumns]);

  const occupiedColumns = useMemo(
    () => pipelineColumns.filter((c) => (counts[c.key] || 0) > 0),
    [counts, pipelineColumns],
  );

  /** Prefer stages that have work; fall back to full nav when the board is empty. */
  const stageNavColumns = occupiedColumns.length > 0 ? occupiedColumns : pipelineColumns

  const inMotionCount = useMemo(
    () => OUTREACH_ACTIVE_COLUMNS.reduce((sum, c) => sum + (counts[c.key] || 0), 0),
    [counts],
  );

  const funnelData = useMemo(
    () =>
      OUTREACH_ACTIVE_COLUMNS.map((c) => ({
        key: c.key,
        label: c.label,
        count: byColumn[c.key]?.length || 0,
      })),
    [byColumn],
  );

  const rows = byColumn[stageParam] || [];

  // If the selected stage is empty, jump to the first stage that has something.
  useEffect(() => {
    if (loading) return;
    if ((counts[stageParam] || 0) > 0) return;
    const next = pipelineColumns.find((c) => (counts[c.key] || 0) > 0);
    if (!next || next.key === stageParam) return;
    const paramsNext = new URLSearchParams(params);
    paramsNext.set("stage", next.key);
    setParams(paramsNext, { replace: true });
  }, [loading, counts, stageParam, params, setParams, pipelineColumns]);

  const engagedThisMonth = useMemo(
    () =>
      clients.filter((c) => {
        const stage = normalizeEngagementStage(c.engagement_stage);
        if (stage === "archive") return false;
        if (stage !== "active" && stage !== "closeout") return false;
        if (!isEngagementBucketStage(stage)) return false;
        return isThisMonth(c.engaged_at);
      }).length,
    [clients],
  );

  const goalPct = Math.min(
    100,
    Math.round((engagedThisMonth / Math.max(monthlyGoal, 1)) * 100),
  );

  const setStageFilter = (stage) => {
    const next = new URLSearchParams(params);
    next.set("stage", stage);
    setParams(next, { replace: true });
  };

  const activeLabel =
    pipelineColumns.find((c) => c.key === stageParam)?.label || "Inquiry";

  return (
    <div className="relative min-h-0 max-w-full overflow-x-clip" data-testid="outreach-page">
      <div className="relative p-4 sm:p-5 md:p-7">
        <MotionSection
          as="header"
          className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="label-eyebrow mb-0">Outreach</div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="neo-cta self-start"
            data-testid="outreach-new-btn"
          >
            <Plus size={16} strokeWidth={1.4} /> Add inquiry
          </button>
        </MotionSection>

        <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <section className="neo-panel p-5" data-testid="outreach-monthly-goal">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <div className="label-eyebrow mb-1">Monthly goal</div>
                <div className="font-heading text-xl tracking-tight !font-light">{monthLabel()}</div>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[var(--t-accent)]">
                <Target size={16} strokeWidth={1.4} />
                <span className="font-heading text-xl tracking-tight !font-light tabular-nums">
                  {engagedThisMonth}
                  <span className="text-[var(--t-meta)]">/</span>
                  {editingGoal ? (
                    <input
                      ref={goalInputRef}
                      type="number"
                      min={1}
                      max={999}
                      inputMode="numeric"
                      disabled={goalSaving}
                      value={goalDraft}
                      onChange={(e) => setGoalDraft(e.target.value)}
                      onBlur={saveMonthlyGoal}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setGoalDraft(String(monthlyGoal));
                          setEditingGoal(false);
                        }
                      }}
                      className={cn(
                        "ml-0.5 inline-block w-[3ch] min-w-[2rem] border-0 border-b border-[var(--t-accent)] bg-transparent p-0",
                        "font-heading text-xl tracking-tight !font-light tabular-nums text-[var(--t-accent)] outline-none",
                        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                      )}
                      aria-label="Monthly engagement goal"
                      data-testid="outreach-goal-input"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setGoalDraft(String(monthlyGoal));
                        setEditingGoal(true);
                      }}
                      className="ml-0.5 border-0 border-b border-transparent bg-transparent p-0 font-heading text-xl tracking-tight !font-light tabular-nums text-[var(--t-meta)] transition-colors hover:border-[var(--t-accent)] hover:text-[var(--t-accent)]"
                      title="Edit monthly goal"
                      data-testid="outreach-goal-edit"
                    >
                      {monthlyGoal}
                    </button>
                  )}
                </span>
              </div>
            </div>
            <p className="mb-3 text-sm text-[var(--t-meta)]">
              New engagements this month. Target: {monthlyGoal}.
            </p>
            <div className="neo-well h-2.5 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-[var(--t-accent)]"
                initial={reduce ? false : { width: 0 }}
                animate={{ width: `${goalPct}%` }}
                transition={{ duration: 0.7 }}
              />
            </div>
          </section>

          <section className="neo-panel p-5" data-testid="outreach-visuals">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <div className="label-eyebrow mb-1">Funnel</div>
                <div className="font-heading text-xl tracking-tight !font-light">In motion</div>
              </div>
              <div className="font-heading text-xl tracking-tight !font-light text-[var(--t-accent)]">
                {inMotionCount}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {funnelData.map((row, idx) => {
                const max = Math.max(1, ...funnelData.map((d) => d.count));
                const width = row.count === 0 ? 10 : Math.max(22, (row.count / max) * 100);
                const active = stageParam === row.key;
                return (
                  <button
                    key={row.key}
                    type="button"
                    onClick={() => setStageFilter(row.key)}
                    className="group text-left"
                    data-testid={`outreach-funnel-${row.key}`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2 text-[11px] uppercase tracking-[0.14em]">
                      <span className={active ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"}>
                        {idx + 1}. {row.label}
                      </span>
                      <span className="tabular-nums text-[var(--t-text)]">
                        {row.count > 0 ? row.count : ""}
                      </span>
                    </div>
                    <div className="neo-well h-2 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: COLUMN_BAR[row.key] }}
                        initial={reduce ? false : { width: 0 }}
                        animate={{ width: `${width}%` }}
                        transition={{ duration: 0.55, delay: idx * 0.05 }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-[var(--t-meta)]">Loading…</p>
        ) : (
          <div
            className="grid grid-cols-1 gap-4 lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start"
            data-testid="outreach-split-rail"
          >
            <aside className="neo-panel px-2 pb-2 pt-3 lg:sticky lg:top-4" data-testid="outreach-stage-nav">
              <div className="label-eyebrow px-3 pb-2">Stages</div>
              <div className="space-y-1">
                {stageNavColumns.map((col) => {
                  const on = stageParam === col.key;
                  const count = counts[col.key] || 0;
                  return (
                    <button
                      key={col.key}
                      type="button"
                      onClick={() => setStageFilter(col.key)}
                      className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left ${
                        on
                          ? "bg-[rgba(0,30,255,0.08)] text-[var(--t-accent)]"
                          : "text-[var(--t-meta)] hover:text-[var(--t-text)]"
                      }`}
                      data-testid={`outreach-column-${col.key}`}
                    >
                      <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em]">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: STAGE_ACCENT[col.key] }}
                        />
                        {col.label}
                      </span>
                      <span className="font-heading text-lg !font-extralight tabular-nums">
                        {count > 0 ? count : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="min-w-0 pt-3">
              <div className="mb-3 flex items-baseline justify-between gap-2">
                <div className="label-eyebrow mb-0">{activeLabel}</div>
                <span className="text-[11px] text-[var(--t-meta)]">
                  {rows.length > 0
                    ? `${rows.length} ${rows.length === 1 ? "inquiry" : "inquiries"}`
                    : ""}
                </span>
              </div>
              {rows.length === 0 ? (
                <div className="py-10" data-testid="outreach-empty" aria-hidden />
              ) : (
                <div className="space-y-2.5">
                  {rows.map((c) => (
                    <ActionRailCell
                      key={c.id}
                      client={c}
                      onOpen={(row) => {
                        navigate(`/admin/outreach/${row.id}`);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCreate ? (
        <CreateProspectModal onClose={() => setShowCreate(false)} onCreated={load} />
      ) : null}
    </div>
  );
}
